import React, {useMemo} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import {CodeBlockRenderer} from './CodeBlockRenderer';
import {SvgRenderer} from './SvgRenderer';
import {preprocessContent, normalizeMarkdownBlocks} from './contentPreprocessor';
import { SvgCodeBlockRenderer } from './SvgCodeBlockRenderer';

export interface MarkdownRendererProps {
  readonly content: string;
  /** 是否正在流式接收 */
  readonly isStreaming?: boolean;
  /** 插入代码回调 */
  readonly onInsertCode?: (code: string, language: string) => void;
  /** 是否显示插入按钮 */
  readonly showInsertCodeBtn?: boolean;
}

/**
 * 检测代码内容是否为 SVG（用于 ReactMarkdown code 组件内部）
 */
const isSvgCode = (code: string): boolean => {
  const trimmed = code.trim();
  return trimmed.startsWith('<svg') && trimmed.includes('</svg>');
};

/**
 * 内部 Markdown 渲染组件（纯 ReactMarkdown 渲染，不含预处理）
 * 使用 remarkGfm 支持表格/删除线/任务列表等 GFM 扩展语法
 * 使用 rehypeRaw 支持内联 HTML 渲染
 */
const MarkdownSegmentRenderer: React.FC<{
  readonly content: string;
  readonly isStreaming?: boolean;
  readonly onInsertCode?: (code: string, language: string) => void;
  readonly showInsertCodeBtn?: boolean;
}> = ({content, isStreaming, onInsertCode, showInsertCodeBtn}) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    rehypePlugins={isStreaming ? [] : [rehypeRaw]}
    components={{
      // 标题组件
      h1: ({children}) => <h1>{children}</h1>,
      h2: ({children}) => <h2>{children}</h2>,
      h3: ({children}) => <h3>{children}</h3>,
      h4: ({children}) => <h4>{children}</h4>,
      h5: ({children}) => <h5>{children}</h5>,
      h6: ({children}) => <h6>{children}</h6>,

      // 段落组件
      p: ({children}) => <p>{children}</p>,

      // 粗体和斜体
      strong: ({children}) => <strong>{children}</strong>,
      em: ({children}) => <em>{children}</em>,

      // 删除线
      del: ({children}) => <del>{children}</del>,

      // 列表组件
      ul: ({children}) => <ul>{children}</ul>,
      ol: ({children}) => <ol>{children}</ol>,
      li: ({children}) => <li>{children}</li>,

      // 代码组件
      code: ({node, inline, className, children, ...props}: any) => {
        const match = /language-(\w+)/.exec(className || '');
        const language = match ? match[1] : '';
        const codeString = String(children).replace(/\n$/, '');

        // ★ 检测是否为 SVG → 使用 SvgCodeBlockRenderer（支持预览/代码切换）
        if (!inline && (language === 'svg' || language === 'xml' && isSvgCode(codeString) || isSvgCode(codeString))) {
          return <SvgCodeBlockRenderer code={codeString} language={language || 'svg'} />;
        }

        // 行内代码
        if (inline) {
          return (
            <code className="inline-code" {...props}>
              {children}
            </code>
          );
        }

        // 代码块
        return (
          <CodeBlockRenderer
            code={codeString}
            language={language || 'text'}
            onInsert={onInsertCode}
            showInsertBtn={showInsertCodeBtn}
          />
        );
      },

      // 代码块容器
      pre: ({children}) => <>{children}</>,

      // 链接组件
      a: ({href, children}) => (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      ),

      // 图片组件
      img: ({src, alt}) => (
        <img src={src} alt={alt || ''} className="md-image" loading="lazy" />
      ),

      // 引用组件
      blockquote: ({children}) => <blockquote>{children}</blockquote>,

      // 表格组件
      table: ({children}) => (
        <div className="table-wrapper">
          <table>{children}</table>
        </div>
      ),
      thead: ({children}) => <thead>{children}</thead>,
      tbody: ({children}) => <tbody>{children}</tbody>,
      tr: ({children}) => <tr>{children}</tr>,
      th: ({children}) => <th>{children}</th>,
      td: ({children}) => <td>{children}</td>,

      // 分割线
      hr: () => <hr />
    }}
  >
    {content}
  </ReactMarkdown>
);

/**
 * 检测流式内容中 SVG 的起始位置
 * 匹配：```svg / ```xml（后跟 <svg）/ 裸 <svg 标签
 * 返回 SVG 起始索引，未检测到返回 -1
 */
const detectSvgStart = (text: string): number => {
  // 优先匹配 markdown 代码块标记
  const codeBlockMatch = /```(?:svg|xml)\s*\n/i.exec(text);
  if (codeBlockMatch) return codeBlockMatch.index;

  // 裸 SVG 标签
  const rawSvgIndex = text.indexOf('<svg');
  if (rawSvgIndex !== -1) return rawSvgIndex;

  return -1;
};

/**
 * Markdown 内容渲染器（含 SVG 预处理）
 *
 * 流程：rawContent → 反转义 → 拆分 segments → 分别渲染
 *
 * ★ svg segment 使用 SvgCodeBlockRenderer（支持 预览/代码 切换）
 *   而非直接用 SvgRenderer 渲染图片
 *
 * 流式策略：
 * - 无 SVG → 实时 Markdown 渲染 + 光标
 * - 检测到 SVG → 渲染 SVG 前的内容 + "图片生成中" 提示 + 光标
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({content, isStreaming, onInsertCode, showInsertCodeBtn}) => {
  const safeContent = content || '';

  // 流式时：检测 SVG 并分离
  const { segments, svgDetected } = useMemo(() => {
    if (!isStreaming) {
      return { segments: preprocessContent(safeContent), svgDetected: false };
    }

    const svgStart = detectSvgStart(safeContent);
    if (svgStart === -1) {
      // 无 SVG，整体作为 markdown
      return {
        segments: [{ type: 'markdown' as const, content: normalizeMarkdownBlocks(safeContent) }],
        svgDetected: false
      };
    }

    // 有 SVG：只渲染 SVG 之前的部分
    const beforeSvg = safeContent.slice(0, svgStart).trimEnd();
    const segs: Array<{ type: 'markdown' | 'svg'; content: string }> = [];
    if (beforeSvg) {
      segs.push({ type: 'markdown', content: normalizeMarkdownBlocks(beforeSvg) });
    }
    return { segments: segs, svgDetected: true };
  }, [safeContent, isStreaming]);

  if (!safeContent.trim()) {
    return <span className="text-content">（空内容）</span>;
  }

  return (
    <div className="markdown-renderer">
      {segments.map((segment, index) => {
        const key = `segment-${index}`;

        if (segment.type === 'svg') {
          return (
            <SvgCodeBlockRenderer
              key={key}
              code={segment.content}
              language="svg"
            />
          );
        }

        return <MarkdownSegmentRenderer 
          key={key} 
          content={segment.content} 
          isStreaming={isStreaming}
          onInsertCode={onInsertCode}
          showInsertCodeBtn={showInsertCodeBtn}
        />;
      })}
      {isStreaming && svgDetected && (
        <div className="svg-generating-hint">
          <i className="fas fa-image" />
          <span>图片生成中</span>
          <span className="typing-cursor" aria-hidden="true">▌</span>
        </div>
      )}
      {isStreaming && !svgDetected && (
        <span className="typing-cursor" aria-hidden="true">▌</span>
      )}
    </div>
  );
};